import { useCallback, useState } from 'react';

import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { ConstructorApi } from '@/features/constructor/api';
import { modifyAnswers } from '@/features/constructorCommon/utils';
import { queryClient } from '@/app/QueryClientProvider';
import { usePopups } from '@/hooks';

export const useQuestionnaireQuestion = ({ questionnaireId }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = useCallback(async ({ question, groupId, dAnswersProp }) => {
    try {
      setIsCreating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : question.visibleConditions;
      const result = await ConstructorApi.createQuestion(
        { ...question, visibleConditions },
        groupId
      );
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Created');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async ({ question, dAnswersProp }) => {
    try {
      setIsUpdating(true);
      const visibleConditions = dAnswersProp
        ? modifyAnswers(dAnswersProp)
        : question.visibleConditions;
      const result = await ConstructorApi.updateQuestion({ ...question, visibleConditions });
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Saved');
      }
    } catch (e) {
      showErrorToast(e);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (question) => {
    try {
      setIsDeleting(true);
      const result = await ConstructorApi.deleteQuestion(question);
      if (result) {
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({
              questionSectionTypeId: questionnaireId
            })
        });
        showSuccessToast('Deleted');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    isCreating,
    isUpdating,
    isDeleting
  };
};
